import { LeaderboardType, Seat, TicketType, TournamentTicket } from '@repo/database/src/types';

import { ChatHistoryResponse } from './chat-types';
import { TournamentListingDto } from './dto/tournaments/tournament.dto';
import {
  LobbyAlertEvent,
  MaintenanceAlertEvent,
  TournamentLevelChangeEvent,
  TournamentPlayerEliminatedEvent,
  TournamentSettingDeletedEvent,
  TournamentStatsLobbyEvent,
} from './event-types';
import { ActiveLeaderboardOverview } from './leaderboard-types';
import { PlayerAction } from './socket-events';
import { AvailableActionKey, Operation } from './table-hand-types';
import { TableStateDto } from './table-state-types';
import { PlayerSeatDto, SeatDto } from './table-types';

export interface LobbyJoinTournamentRoomPayload {
  tournamentId: string;
}

export interface LobbyJoinTournamentRoomAck {
  success: boolean;
  tournament?: TournamentListingDto;
  error?: string;
}

export interface LobbyLeaveTournamentRoomPayload {
  tournamentId: string;
}

export interface LobbyCancelSeatReadyPayload {
  tableId: string;
}

export interface LobbyTableUrlUpdatedPayload {
  tableId: string;
  url: string;
}

export interface LobbyDuplicateLoginPayload {
  visitorId: string;
}

export interface LobbyUserTournamentDollarsAddedPayload {
  newBalance: number;
}

export interface LobbyUserTournamentTicketAddedPayload {
  ticket: TournamentTicket;
}

export type LobbyAlertUpdatedPayload = LobbyAlertEvent['alert'];
export type LobbyMaintenanceConfigUpdatedPayload = MaintenanceAlertEvent['alert'];
export type LobbyTournamentSettingDeletedPayload = Omit<TournamentSettingDeletedEvent, 'type'>;

export interface LobbyJoinLeaderboardRoomSuccessPayload {
  leaderboards: ActiveLeaderboardOverview | null;
  userMessages: Record<LeaderboardType, string | null>;
}

export interface TournamentStartPayload {
  tournamentId: string;
  tableId: string;
  tableName?: string | null;
  tableUrl?: string | null;
  seat: number;
  url?: string | null;
}

export interface TournamentRegistrationSuccessPayload {
  tournamentId: string;
  userId: string;
  alias: string;
  countryCode: string | null;
  ticketId?: string | null;
}

export interface TournamentUnregistrationSuccessPayload {
  tournamentId: string;
  userId: string;
  ticketId?: string | null;
}

export type TournamentPlayerEliminatedPayload = Omit<TournamentPlayerEliminatedEvent, 'type'>;

export interface TournamentEndedPayload {
  tournamentId: string;
  tableId?: string;
  winnerInfo?: {
    userId: string;
    finalPosition: number;
    prize: number | null;
  };
}

export type TournamentLevelChangePayload = Omit<TournamentLevelChangeEvent, 'type'>;

export interface TournamentCreatedPayload {
  tournamentId: string;
  tournament: TournamentListingDto;
}

export interface TournamentCancelledPayload {
  tournamentId: string;
}

export type TournamentStatsLobbyPayload = Omit<TournamentStatsLobbyEvent, 'type'>;

export interface ChatGetChatsPayload {
  tableId: string;
  chats: ChatHistoryResponse[];
}

export type TablePlayerActionDataMap = {
  [PlayerAction.FOLD]: null;
  [PlayerAction.CHECK]: null;
  [PlayerAction.CALL]: null;
  [PlayerAction.BET]: { amount?: number };
  [PlayerAction.RAISE]: { amount?: number };
  [PlayerAction.BRING_IN]: null;
  [PlayerAction.COMPLETE]: { amount?: number };
  [PlayerAction.DISCARD]: { cards?: number[] };
  [PlayerAction.TIMEBANK]: null;
  [PlayerAction.STAND_PAT]: null;
  [PlayerAction.REVEAL_FOLDED_CARDS]: { cardIndexes: number[] };
  [PlayerAction.LEAVE_TABLE]: null;
  [PlayerAction.SET_WAIT_FOR_BIG_BLIND]: { waiting: boolean };
  [PlayerAction.CONFIRM_SEAT]: { timeBank: number };
  [PlayerAction.START_ADDING_CHIPS]: { amount?: number };
  [PlayerAction.SET_TIMEBANK_AUTO_ENABLED_SERVER]: { enabled: boolean };
  [PlayerAction.RUNOUT_COUNT_RESPONSE]: { count: number; type?: PlayerAction.RUNOUT_COUNT_RESPONSE };
  [PlayerAction.RUNOUT_AGREEMENT_RESPONSE]: { agree: boolean };
  [PlayerAction.SIT_OUT]: null;
  [PlayerAction.SIT_OUT_NEXT_BB]: null;
  [PlayerAction.SIT_IN]: null;
  // OFC actions
  [PlayerAction.OFC_PLACE_CARDS]: { placements: Array<{ card: string; row: string; position: number }> };
  [PlayerAction.OFC_USE_TIMEBANK]: null;
  [PlayerAction.OFC_SYNC_PENDING_PLACEMENTS]: { placements: Array<{ card: string; row: string; position: number }> };
};

export type TablePlayerActionPayload = {
  [Action in PlayerAction]: {
    type: Action;
    tableId: string;
    handId?: string;
  } & (TablePlayerActionDataMap[Action] extends null
    ? { payload?: null }
    : { payload: TablePlayerActionDataMap[Action] });
}[PlayerAction];

export type TableStatePayload = Partial<TableStateDto>;

export interface TableSeatReservedPayload {
  tableId: string;
  userId: string;
  seat: SeatDto;
  playerSeat?: PlayerSeatDto;
}

export interface TableLeftPayload extends Omit<TableSitPayload, 'seat'> {
  seat?: Seat;
}

export interface TablePlayerTurnPayload {
  seat: number;
  timeRemaining: number;
  timer: number;
  usingTimebank: boolean;
  usingDisconnectionTimebank?: boolean;
}

export type TableConfirmPayload = string;

export interface TableMyTurnPayload {
  tableId: string;
  handId?: string;
  data: {
    availableActions: AvailableActionKey[];
  };
}

export interface TableActionPerformedPayload {
  tableId: string;
  handId?: string;
  lastOperation?: Operation & { seat?: number; action?: string; amount?: number };
}

export interface TableSitPayload {
  tableId: string;
  userId: string;
  seat?: number;
  reason?: string;
}

export interface TableHandFinishedPayload {
  tableId: string;
  handId: string | null;
}

export interface TableErrorPayload {
  tableId?: string;
  handId: string | null;
  error: unknown;
}

export interface TableClientVersionOutdatedPayload {
  requiredVersion: number;
}

export interface TableRebuyProcessedPayload {
  amount: number;
  txId: string;
  tableId?: string;
  willBeAppliedNextHand: boolean;
}

export interface TableRunoutCountRequestPayload {
  options: number[];
  decidingSeat: number;
}

export interface TableRunoutAgreementRequestPayload {
  count: number;
  decidingSeat: number;
}

export interface TableRunoutWaitingPayload {
  decidingUserId: string;
  decidingSeat: number;
  status: 'selecting' | 'agreeing' | 'waiting';
  count?: number;
}

export interface TableAddingChipsPayload {
  userId: string;
}

export interface TableAddingChipsEndPayload {
  amount?: number;
  userId: string;
}

export interface TableTournamentPausedPayload {
  timeLeftInLevel: number;
}

export interface TableTournamentResumingAtPayload {
  resumingAt: string;
}

export interface TableTournamentResumedPayload {
  levelEndAt: string;
}

export interface TableTournamentCancelledPayload {
  tableId: string;
}

export interface TableTournamentEndedPayload {
  tableId: string;
  tournamentId: string;
  winnerInfo: {
    userId: string;
    finalPosition: number | null;
    prize: number | null;
  };
}

export interface TableTournamentLevelChangePayload {
  tableId: string;
}

export interface TableChangeTablePayload {
  tableId: string;
  seat?: number;
}

export interface TableSatelliteTournamentEndedPayload {
  tournamentId: string;
  winners: Array<{
    userId: string;
    prizeType: TicketType;
    amount: number;
    parentTournamentId: string | null;
    isTournamentDollars: boolean;
  }>;
}

export interface TableTournamentBreakPayload {
  tableId: string;
  breakEndAt?: string | null;
}

// ============================================================================
// OFC Event Payloads
// ============================================================================

export interface OFCHandStartedPayload {
  handId: string;
  playerCount: number;
  dealerSeat: number;
  fantasyPlayers: string[];
}

export interface OFCCardsDealtPayload {
  handId: string | null;
  round: number;
  isInitial: boolean;
}

export interface OFCHandScoredPayload {
  handId: string | null;
  results: unknown; // HandResults from ofc module
}

export interface OFCPlacementTimerStartedPayload {
  userId: string;
  deadline: number;
}

export interface OFCPlayerTimerPayload {
  seat: number;
  userId: string;
  timeRemaining: number;
  timer?: number;
  baseTimeout?: number;
  timebankRemaining?: number;
  disconnectionTimebankRemaining?: number;
  usingTimebank?: boolean;
  usingDisconnectionTimebank?: boolean;
  stopped?: boolean;
}

export interface OFCPlacementConfirmedPayload {
  userId: string;
  board: unknown;
}

export interface OFCPlacementErrorPayload {
  userId: string;
  error: string;
}

export interface OFCAllPlacedPayload {
  boards: unknown;
}

export interface OFCCardsRevealedPayload {
  userId: string;
}

export interface OFCBoardsRevealedPayload {
  boards: unknown;
}

export interface OFCRowRevealedPayload {
  row: 'front' | 'middle' | 'back';
  boards: Array<{
    seat: number;
    userId: string;
    cards: unknown;
  }>;
}

export interface OFCFantasyCardsRevealedPayload {
  userId: string;
}

export interface OFCFantasyTimerStartedPayload {
  userId: string;
  deadline: number;
}

export interface OFCPlayerPreConfirmedPayload {
  userId: string;
}

export interface OFCPlayerAutoConfirmedPayload {
  userId: string;
}

export interface OFCLeaveRequestDeferredPayload {
  reason: string;
  message: string;
}

export interface OFCSitOutDeferredPayload {
  reason: string;
  message: string;
}

export interface OFCSitInDeferredPayload {
  reason: string;
  message: string;
}

export interface OFCPendingProcessedPayload {
  processedLeaveRequests: string[];
  processedSitOutRequests: string[];
  processedSitInRequests: string[];
}

export interface OFCRebuyPromptPayload {
  reason: string;
  message: string;
  currentStack: number;
  canLeaveAfterFantasy?: boolean;
}

export interface OFCAutoSatOutPayload {
  reason: string;
  canRebuy: boolean;
}

export interface OFCFantasyForfeitedPayload {
  reason: string;
  message: string;
}

export interface OFCFantasyCancelledPayload {
  reason: string;
  message: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OFCTableRestartedPayload {}
